import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MarkdownService } from '../../../../services/markdown.service';
import { LocalizationPipe } from '../../../../pipe/localization-pipe';
import { LocalizationService } from '../../../../services/localizationService';
import { ToastrService } from 'ngx-toastr';
import { FileUploadService } from '../../../../services/apps/file/file-upload.service';

export interface MarkdownViewerData {
  organisationId: number;
  fileId: number;
  fileName: string;
  fileUrl?: string;
}

@Component({
  selector: 'app-markdown-viewer-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatTooltipModule,
    TablerIconsModule,
    LocalizationPipe
  ],
  template: `
    <div class="markdown-modal-container">
      <!-- Header -->
      <mat-toolbar class="markdown-modal-header">
        <div class="header-content">
          <h2 class="modal-title">{{ data.fileName }}</h2>
          <div class="header-actions">
            <button
              *ngIf="data.fileUrl"
              mat-icon-button
              (click)="goToFileUrl()"
              [matTooltip]="'MarkdownViewer.GoToLink' | localize"
              class="action-button">
              <i-tabler name="external-link" class="icon-18"></i-tabler>
            </button>
            <button
              mat-icon-button
              (click)="closeModal()"
              [matTooltip]="'MarkdownViewer.Close' | localize"
              class="close-button">
              <i-tabler name="x" class="icon-18"></i-tabler>
            </button>
          </div>
        </div>
      </mat-toolbar>

      <!-- Content -->
      <div class="markdown-modal-content">
        <div *ngIf="isLoading" class="loading-container">
          <mat-spinner diameter="40"></mat-spinner>
          <p>{{ "MarkdownViewer.Loading" | localize }}</p>
        </div>

        <div *ngIf="error" class="error-container">
          <i-tabler name="alert-circle" class="error-icon"></i-tabler>
          <p class="error-message">{{ error }}</p>
          <button mat-raised-button color="primary" (click)="loadMarkdownContent()">
            {{ "MarkdownViewer.Retry" | localize }}
          </button>
        </div>

        <div *ngIf="!isLoading && !error" class="markdown-content" [innerHTML]="markdownContent">
        </div>
      </div>
    </div>
  `,
  styles: [`
    .markdown-modal-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      background: #f5f5f5;
      border-radius: 12px;
      overflow: hidden;
    }

    .markdown-modal-header {
      background: white;
      color: #333;
      padding: 16px 24px;
      border-radius: 12px 12px 0 0;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      border-bottom: 1px solid #e0e0e0;
      flex-shrink: 0;
    }

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    .modal-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #333;
    }

    .header-actions {
      display: flex;
      gap: 8px;
    }

    .action-button {
      color: #333;
      transition: all 0.2s ease;
    }

    .action-button:hover {
      background: rgba(0, 0, 0, 0.04);
      transform: scale(1.05);
    }

    .close-button {
      color: #333;
      transition: all 0.2s ease;
    }

    .close-button:hover {
      background: rgba(0, 0, 0, 0.04);
      transform: scale(1.05);
    }

    .markdown-modal-content {
      flex: 1;
      padding: 24px;
      overflow-y: auto;
      background: white;
      border-radius: 0 0 12px 12px;
      box-shadow: 0 -2px 8px rgba(0,0,0,0.05);
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 200px;
      gap: 16px;
    }

    .error-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 200px;
      gap: 16px;
      text-align: center;
    }

    .error-icon {
      font-size: 48px;
      color: #f44336;
    }

    .error-message {
      color: #666;
      margin: 0;
    }

    .markdown-content {
      line-height: 1.8;
      color: #333;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .markdown-content h1 {
      font-size: 2.2em;
      margin-bottom: 0.8em;
      margin-top: 1.5em;
      color: #333;
      border-bottom: 3px solid #e0e0e0;
      padding-bottom: 0.5em;
      font-weight: 700;
    }

    .markdown-content h2 {
      font-size: 1.8em;
      margin-top: 2em;
      margin-bottom: 0.6em;
      color: #333;
      border-bottom: 2px solid #e0e0e0;
      padding-bottom: 0.4em;
      font-weight: 600;
    }

    .markdown-content h3 {
      font-size: 1.5em;
      margin-top: 1.8em;
      margin-bottom: 0.5em;
      color: #333;
      font-weight: 600;
    }

    .markdown-content h4 {
      font-size: 1.3em;
      margin-top: 1.5em;
      margin-bottom: 0.4em;
      color: #333;
      font-weight: 600;
    }

    .markdown-content p {
      margin-bottom: 1.2em;
      text-align: justify;
    }

    .markdown-content strong {
      font-weight: 700;
      color: #333;
    }

    .markdown-content a {
      color: #1976d2;
      text-decoration: none;
      border-bottom: 1px solid transparent;
      transition: all 0.2s ease;
    }

    .markdown-content a:hover {
      color: #28a745;
      border-bottom-color: #28a745;
    }

    .markdown-content ul, .markdown-content ol {
      margin-bottom: 1.2em;
      padding-left: 2.5em;
    }

    .markdown-content li {
      margin-bottom: 0.5em;
      line-height: 1.6;
    }

    .markdown-content blockquote {
      border-left: 4px solid #1976d2;
      margin: 1.5em 0;
      padding: 1em 1.5em;
      background: #f8f9fa;
      border-radius: 0 8px 8px 0;
      color: #6c757d;
      font-style: italic;
    }

    .markdown-content code {
      background-color: #f8f9fa;
      padding: 0.3em 0.6em;
      border-radius: 4px;
      font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
      font-size: 0.9em;
      color: #f44336;
      border: 1px solid #e0e0e0;
    }

    .markdown-content pre {
      background-color: #f8f9fa;
      color: #333;
      padding: 1.5em;
      border-radius: 8px;
      overflow-x: auto;
      margin: 1.5em 0;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      border: 1px solid #e0e0e0;
    }

    .markdown-content pre code {
      background: none;
      padding: 0;
      border: none;
      color: inherit;
    }

    .markdown-content hr {
      border: none;
      height: 2px;
      background: linear-gradient(90deg, transparent, #e0e0e0, transparent);
      margin: 2em 0;
    }

    .markdown-content img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      margin: 1em 0;
    }

    .markdown-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 1.5em 0;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      border: 1px solid #e0e0e0;
    }

    .markdown-content th {
      background-color: #f8f9fa;
      padding: 12px;
      text-align: left;
      font-weight: 600;
      color: #333;
      border-bottom: 2px solid #e0e0e0;
    }

    .markdown-content td {
      padding: 12px;
      border-bottom: 1px solid #e0e0e0;
      color: #495057;
    }

    .markdown-content tr:hover {
      background-color: #f8f9fa;
    }

    .icon-18 {
      width: 18px;
      height: 18px;
    }

    /* Responsive design */
    @media (max-width: 768px) {
      .markdown-modal-content {
        padding: 16px;
      }
      
      .markdown-content {
        font-size: 14px;
        padding: 0 10px;
      }
      
      .markdown-content h1 {
        font-size: 1.8em;
      }
      
      .markdown-content h2 {
        font-size: 1.5em;
      }
    }

    /* Dark theme support */
    @media (prefers-color-scheme: dark) {
      .markdown-modal-container {
        background-color: #121212;
      }

      .markdown-modal-header {
        background: #1e1e1e;
        color: #fff;
        border-bottom-color: #404040;
      }

      .modal-title {
        color: #fff;
      }

      .action-button,
      .close-button {
        color: #fff;
      }

      .action-button:hover,
      .close-button:hover {
        background: rgba(255, 255, 255, 0.04);
      }

      .markdown-modal-content {
        background: #1e1e1e;
      }

      .error-message {
        color: #ccc;
      }

      .markdown-content {
        color: #fff;
      }

      .markdown-content h1,
      .markdown-content h2,
      .markdown-content h3,
      .markdown-content h4,
      .markdown-content strong {
        color: #fff;
      }

      .markdown-content h1,
      .markdown-content h2 {
        border-bottom-color: #404040;
      }

      .markdown-content a {
        color: #1976d2;
      }

      .markdown-content a:hover {
        color: #28a745;
      }

      .markdown-content blockquote {
        background: #2d2d2d;
        color: #ccc;
        border-left-color: #1976d2;
      }

      .markdown-content code {
        background-color: #2d2d2d;
        color: #f44336;
        border-color: #404040;
      }

      .markdown-content pre {
        background-color: #2d2d2d;
        color: #e0e0e0;
        border-color: #404040;
      }

      .markdown-content hr {
        background: linear-gradient(90deg, transparent, #404040, transparent);
      }

      .markdown-content table {
        border-color: #404040;
      }

      .markdown-content th {
        background-color: #2d2d2d;
        color: #fff;
        border-bottom-color: #404040;
      }

      .markdown-content td {
        color: #e0e0e0;
        border-bottom-color: #404040;
      }

      .markdown-content tr:hover {
        background-color: #333;
      }
    }
  `]
})
export class MarkdownViewerModalComponent implements OnInit {
  isLoading = true;
  error: string | null = null;
  markdownContent: any = '';

  constructor(
    public dialogRef: MatDialogRef<MarkdownViewerModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MarkdownViewerData,
    private markdownService: MarkdownService,
    private toastService: ToastrService,
    private fileService: FileUploadService,
    private localizationService: LocalizationService
  ) {}

  ngOnInit(): void {
    this.loadMarkdownContent();
  }

  loadMarkdownContent(): void {
    this.isLoading = true;
    this.error = null;

    this.fileService.getScraperFileAsMarkdown(this.data.organisationId, this.data.fileId).subscribe({
      next: (markdownContent: string) => {
        if (markdownContent && markdownContent.trim()) {
          this.markdownContent = this.markdownService.render(markdownContent);
        } else {
          this.error = this.localizationService.getLocalizationValue('MarkdownViewer.NoContent');
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Markdown content alma hatası:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        
        let errorMessage = this.localizationService.getLocalizationValue('MarkdownViewer.LoadError');
        if (error.status === 500) {
          errorMessage = this.localizationService.getLocalizationValue('MarkdownViewer.BackendError');
        } else if (error.status === 404) {
          errorMessage = this.localizationService.getLocalizationValue('MarkdownViewer.NotFound');
        } else if (error.status === 403) {
          errorMessage = this.localizationService.getLocalizationValue('MarkdownViewer.AccessDenied');
        }
        
        this.error = errorMessage;
        this.isLoading = false;
      }
    });
  }

  goToFileUrl(): void {
    if (this.data.fileUrl) {
      window.open(this.data.fileUrl, '_blank', 'noopener,noreferrer');
    }
  }

  closeModal(): void {
    this.dialogRef.close();
  }
} 